"use client";

import { cn, Loader } from "@devhop/ui";
import type { ReactNode } from "react";

type InventorySkuStockBannerProps = {
  productName: string;
  shopSku: string;
  /** ບັນທັດຮອງໃຕ້ SKU (ເຊັ່ນ ໂໝດຕິດຕາມ, ສາຂາ) */
  metaLines?: string[];
  stockTitle?: string;
  /** ພື້ນທີ່ຂວາ: ຕົວເລກໃຫຍ່, ໂຫຼດ, ຫຼືຂໍ້ຄວາມເຕືອນ */
  stockArea: ReactNode;
  /** ບັນທັດລຸ່ມຂວາ (ເຊັ່ນ ລວມທົ່ວຮ້ານ) */
  footnote?: ReactNode;
  className?: string;
};

/**
 * ແບນເນີສິນຄ້າ + ຍອດຄົງເຫຼືອແບບແຖບຂ້າງ Tech Blue (ສອດຄ່ອງ PARTZYNC)
 */
export function InventorySkuStockBanner({
  productName,
  shopSku,
  metaLines,
  stockTitle = "ຄົງເຫຼືອປັດຈຸບັນ",
  stockArea,
  footnote,
  className,
}: InventorySkuStockBannerProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-border bg-muted/25",
        className,
      )}
    >
      <div className="flex min-w-0 gap-0">
        <div
          className="min-h-[5rem] w-1 shrink-0 self-stretch bg-secondary"
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 sm:flex-row sm:justify-between sm:gap-8">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[0.65rem] text-muted-foreground/90 uppercase tracking-[0.14em]">
              ສິນຄ້າທີ່ເລືອກ
            </p>
            <p className="mt-1.5 font-bold font-heading text-foreground text-xl leading-snug sm:text-2xl">
              {productName}
            </p>
            <p className="mt-2 font-medium text-secondary text-sm">
              SKU: {shopSku}
            </p>
            {metaLines?.map((line) => (
              <p
                key={line}
                className="mt-1 text-muted-foreground text-xs leading-snug"
              >
                {line}
              </p>
            ))}
          </div>
          <div className="flex min-w-0 flex-col sm:max-w-[min(100%,14rem)] sm:items-end sm:text-right">
            <p className="font-semibold text-[0.65rem] text-muted-foreground/90 uppercase tracking-[0.14em]">
              {stockTitle}
            </p>
            <div className="mt-2 w-full sm:w-auto">{stockArea}</div>
            {footnote ? (
              <div className="mt-2 text-muted-foreground text-xs leading-snug sm:text-right">
                {footnote}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

type InventoryStockHeroProps = {
  value: string;
  /** ສະແດງຄູ່ກັບຕົວເລກ (ບໍ່ໃຊ້ກັບ "—") */
  unitLabel?: string;
};

/** ຕົວເລກໃຫຍ່ + ຫົວໜ່ວຍຂ້າງ (ແບບ mockup) */
export function InventoryStockHero({
  value,
  unitLabel = "ຊິ້ນ",
}: InventoryStockHeroProps) {
  const showUnit = value !== "—" && value.trim() !== "";
  return (
    <div className="flex flex-wrap items-baseline gap-2 sm:justify-end">
      <span className="font-bold font-heading text-3xl text-foreground tabular-nums tracking-tight sm:text-4xl">
        {value}
      </span>
      {showUnit ? (
        <span className="pb-0.5 font-medium text-secondary text-sm">
          {unitLabel}
        </span>
      ) : null}
    </div>
  );
}

export function InventoryStockLoading() {
  return (
    <div className="flex items-center gap-2 sm:justify-end">
      <Loader />
      <span className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</span>
    </div>
  );
}
